import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  DEFAULT_THEME_MODE,
  THEME_CHANGE_EVENT,
  THEME_PRIMARY_VAR,
  THEME_SECONDARY_VAR,
  THEME_STORAGE_KEY,
  ThemeMode
} from "constants/theme";
import {
  applyThemeModeToDocument,
  getStoredThemeColors,
  getStoredThemeMode,
  persistThemeMode
} from "utilities/themeColors";

type UseThemeColorPickerReturn = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  primaryColor: string;
  secondaryColor: string;
  themeMode: ThemeMode;
  panelRef: React.MutableRefObject<HTMLDivElement | null>;
  handleReset: () => void;
  toggleThemeMode: () => void;
  handlePrimaryChange: (color: string) => void;
  handleSecondaryChange: (color: string) => void;
  setPrimaryColor: Dispatch<SetStateAction<string>>;
  setSecondaryColor: Dispatch<SetStateAction<string>>;
  setThemeMode: Dispatch<SetStateAction<ThemeMode>>;
};

const parseColorToRgbComponents = (color: string): string | null => {
  if (!color) return null;

  const trimmed = color.trim().toLowerCase();

  // HEX formats: #rgb or #rrggbb
  if (trimmed.startsWith("#")) {
    let hex = trimmed.slice(1);

    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((ch) => ch + ch)
        .join("");
    }

    if (hex.length !== 6) return null;

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    if ([r, g, b].some((v) => Number.isNaN(v))) return null;

    return `${r} ${g} ${b}`;
  }

  // rgb() or rgba()
  if (trimmed.startsWith("rgb")) {
    const match = trimmed.match(/rgba?\(([^)]+)\)/);
    if (!match) return null;

    const parts = match[1]
      .split(",")
      .map((p) => p.trim())
      .slice(0, 3);

    if (parts.length !== 3) return null;

    const [r, g, b] = parts.map((p) => parseInt(p, 10));
    if ([r, g, b].some((v) => Number.isNaN(v))) return null;

    return `${r} ${g} ${b}`;
  }

  return null;
};

const useThemeColorPicker = (): UseThemeColorPickerReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY_COLOR);
  const [hasColorsHydrated, setHasColorsHydrated] = useState(false);
  const [themeMode, setThemeMode] = useState<ThemeMode>(DEFAULT_THEME_MODE);
  const [hasThemeHydrated, setHasThemeHydrated] = useState(false);
  const latestColorsRef = useRef({ primary: DEFAULT_PRIMARY_COLOR, secondary: DEFAULT_SECONDARY_COLOR });
  // Tracks which theme mode the current primary/secondary colors belong to
  const colorsModeRef = useRef<ThemeMode>(DEFAULT_THEME_MODE);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    // Hydrate colors for the currently stored theme mode
    const mode = getStoredThemeMode();
    const storedColors = getStoredThemeColors(mode);

    if (storedColors) {
      const { primary, secondary } = storedColors;

      if (primary) {
        setPrimaryColor(primary);
      }

      if (secondary) {
        setSecondaryColor(secondary);
      }

      colorsModeRef.current = mode;
      setHasColorsHydrated(true);
      return;
    }

    const computedStyles = getComputedStyle(document.documentElement);
    const currentPrimary = computedStyles.getPropertyValue(THEME_PRIMARY_VAR).trim() || DEFAULT_PRIMARY_COLOR;
    const currentSecondary = computedStyles.getPropertyValue(THEME_SECONDARY_VAR).trim() || DEFAULT_SECONDARY_COLOR;

    setPrimaryColor(currentPrimary);
    setSecondaryColor(currentSecondary);
    colorsModeRef.current = mode;
    setHasColorsHydrated(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const storedMode = getStoredThemeMode();
    setThemeMode(storedMode);
    applyThemeModeToDocument(storedMode);
    setHasThemeHydrated(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    document.documentElement.style.setProperty(THEME_PRIMARY_VAR, primaryColor);

    const rgb = parseColorToRgbComponents(primaryColor);
    if (rgb) {
      document.documentElement.style.setProperty(`${THEME_PRIMARY_VAR}-rgb`, rgb);
    }
  }, [primaryColor]);

  useEffect(() => {
    document.documentElement.style.setProperty(THEME_SECONDARY_VAR, secondaryColor);

    const rgb = parseColorToRgbComponents(secondaryColor);
    if (rgb) {
      document.documentElement.style.setProperty(`${THEME_SECONDARY_VAR}-rgb`, rgb);
    }
  }, [secondaryColor]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!hasColorsHydrated) {
      return;
    }

    // Persist colors per theme mode (dark / light) based on the mode
    // that the current colors belong to, not just the latest themeMode
    try {
      const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
      let byMode: any = {};

      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          byMode = parsed;
        }
      }

      const targetMode = colorsModeRef.current;

      byMode[targetMode] = {
        primary: primaryColor,
        secondary: secondaryColor
      };

      window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(byMode));
    } catch {
      // ignore storage errors
    }
  }, [primaryColor, secondaryColor, hasColorsHydrated, themeMode]);

  useEffect(() => {
    latestColorsRef.current = {
      primary: primaryColor,
      secondary: secondaryColor
    };
  }, [primaryColor, secondaryColor]);

  const syncColorsWithCssDefaults = useCallback(() => {
    if (typeof window === "undefined") {
      setPrimaryColor(DEFAULT_PRIMARY_COLOR);
      setSecondaryColor(DEFAULT_SECONDARY_COLOR);
      return;
    }

    document.documentElement.style.removeProperty(THEME_PRIMARY_VAR);
    document.documentElement.style.removeProperty(THEME_SECONDARY_VAR);

    const computedStyles = getComputedStyle(document.documentElement);
    const cssPrimary = computedStyles.getPropertyValue(THEME_PRIMARY_VAR).trim() || DEFAULT_PRIMARY_COLOR;
    const cssSecondary = computedStyles.getPropertyValue(THEME_SECONDARY_VAR).trim() || DEFAULT_SECONDARY_COLOR;

    setPrimaryColor(cssPrimary);
    setSecondaryColor(cssSecondary);
  }, []);

  const handleReset = () => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    }

    syncColorsWithCssDefaults();
  };

  useEffect(() => {
    if (!hasThemeHydrated) {
      return;
    }

    applyThemeModeToDocument(themeMode);
    persistThemeMode(themeMode);

    // When theme mode changes, load mode-specific colors if present,
    // otherwise fall back to CSS defaults for that mode.
    const modeSpecificColors = getStoredThemeColors(themeMode);
    if (modeSpecificColors && (modeSpecificColors.primary || modeSpecificColors.secondary)) {
      if (modeSpecificColors.primary) {
        setPrimaryColor(modeSpecificColors.primary);
      }
      if (modeSpecificColors.secondary) {
        setSecondaryColor(modeSpecificColors.secondary);
      }
    } else {
      syncColorsWithCssDefaults();
    }

    // Now the in-memory colors belong to this new theme mode
    colorsModeRef.current = themeMode;
  }, [themeMode, hasThemeHydrated, syncColorsWithCssDefaults]);

  const toggleThemeMode = () => {
    setThemeMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const detail = {
      primaryColor,
      secondaryColor,
      timestamp: Date.now()
    };

    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail }));
  }, [primaryColor, secondaryColor]);

  const handlePrimaryChange = (value: string) => {
    setPrimaryColor(value);
  };

  const handleSecondaryChange = (value: string) => {
    setSecondaryColor(value);
  };

  return {
    isOpen,
    setIsOpen,
    primaryColor,
    secondaryColor,
    themeMode,
    panelRef,
    handleReset,
    toggleThemeMode,
    handlePrimaryChange,
    handleSecondaryChange,
    setPrimaryColor,
    setSecondaryColor,
    setThemeMode
  };
};

export default useThemeColorPicker;

